import { ConsultDiarizationDto, DiarizationDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'
import {
    executeQuery,
    executeMutation,
    fetchVersion,
} from 'app/api/client/graphqlClient'
import {
    consultDiarizationDtosByConsultId,
    createConsultDiarizationDtoMutation,
    updateConsultDiarizationDtoMutation,
    deleteConsultDiarizationDtoMutation,
} from 'app/graphql/customQueries'
import { isNotDeleted } from './softDelete'

/**
 * Repository interface for managing diarization data.
 * Provides CRUD operations for ConsultDiarizationDto records.
 */
export interface DiarizationRepository {
    /**
     * Create a new diarization record for a consult.
     * @param consultId - The ID of the consult
     * @param diarization - Array of diarization segments
     * @returns The created ConsultDiarizationDto
     */
    create(
        consultId: string,
        diarization: DiarizationDto[]
    ): Promise<ConsultDiarizationDto>

    /**
     * Get diarization record by consult ID.
     * @param consultId - The ID of the consult
     * @returns The ConsultDiarizationDto or null if not found
     */
    getByConsultId(consultId: string): Promise<ConsultDiarizationDto | null>

    /**
     * Update diarization for a consult.
     * Creates a new record if one doesn't exist.
     * @param consultId - The ID of the consult
     * @param diarization - Array of diarization segments
     * @returns The updated ConsultDiarizationDto
     */
    update(
        consultId: string,
        diarization: DiarizationDto[]
    ): Promise<ConsultDiarizationDto>

    /**
     * Delete diarization record for a consult.
     * @param consultId - The ID of the consult
     */
    delete(consultId: string): Promise<void>
}

interface ConsultDiarizationDtoResult {
    id: string
    consultId: string
    owner?: string
    diarization: DiarizationDto[]
}

interface ConsultDiarizationDtosByConsultIdResult {
    items: ConsultDiarizationDtoResult[]
    nextToken: string | null
}

/**
 * GraphQL API implementation of DiarizationRepository.
 * Manages diarization data via AppSync GraphQL operations.
 */
export class AmplifyDiarizationRepository implements DiarizationRepository {
    async create(
        consultId: string,
        diarization: DiarizationDto[]
    ): Promise<ConsultDiarizationDto> {
        try {
            logger.debug('Creating diarization record', {
                action: 'create_diarization',
                metadata: { consultId, segmentCount: diarization.length },
            })

            const created = await executeMutation<ConsultDiarizationDto>(
                createConsultDiarizationDtoMutation,
                {
                    input: {
                        consultId,
                        diarization,
                    },
                },
                'createConsultDiarizationDto'
            )

            logger.info('Diarization record created successfully', {
                action: 'create_diarization_success',
                metadata: {
                    consultId,
                    diarizationId: created.id,
                    segmentCount: diarization.length,
                },
            })

            return created
        } catch (error) {
            logger.error(
                'Failed to create diarization record',
                error as Error,
                {
                    action: 'create_diarization_error',
                    metadata: { consultId },
                }
            )
            throw error
        }
    }

    async getByConsultId(
        consultId: string
    ): Promise<ConsultDiarizationDto | null> {
        try {
            logger.debug('Querying diarization by consultId', {
                action: 'get_diarization',
                metadata: { consultId },
            })

            const result =
                await executeQuery<ConsultDiarizationDtosByConsultIdResult>(
                    consultDiarizationDtosByConsultId,
                    { consultId },
                    'consultDiarizationDtosByConsultId'
                )

            const items = result.items.filter(isNotDeleted)

            if (items.length === 0) {
                logger.debug('No diarization found for consult', {
                    action: 'get_diarization_not_found',
                    metadata: { consultId },
                })
                return null
            }

            if (items.length > 1) {
                logger.warn('Multiple diarization records found for consult', {
                    action: 'get_diarization_multiple',
                    metadata: { consultId, count: items.length },
                })
            }

            return items[0] as unknown as ConsultDiarizationDto
        } catch (error) {
            logger.error('Failed to query diarization', error as Error, {
                action: 'get_diarization_error',
                metadata: { consultId },
            })
            throw error
        }
    }

    async update(
        consultId: string,
        diarization: DiarizationDto[]
    ): Promise<ConsultDiarizationDto> {
        try {
            const existing = await this.getByConsultId(consultId)

            if (!existing) {
                logger.debug('No existing diarization, creating new', {
                    action: 'update_diarization_create',
                    metadata: { consultId },
                })
                return this.create(consultId, diarization)
            }

            const _version = await fetchVersion(
                'ConsultDiarizationDto',
                existing.id
            )

            const updated = await executeMutation<ConsultDiarizationDto>(
                updateConsultDiarizationDtoMutation,
                {
                    input: {
                        id: existing.id,
                        diarization,
                        _version,
                    },
                },
                'updateConsultDiarizationDto'
            )

            logger.info('Diarization record updated successfully', {
                action: 'update_diarization_success',
                metadata: { consultId, segmentCount: diarization.length },
            })

            return updated
        } catch (error) {
            logger.error('Failed to update diarization', error as Error, {
                action: 'update_diarization_error',
                metadata: { consultId },
            })
            throw error
        }
    }

    async delete(consultId: string): Promise<void> {
        try {
            const existing = await this.getByConsultId(consultId)

            if (!existing) {
                logger.debug('No diarization to delete', {
                    action: 'delete_diarization_not_found',
                    metadata: { consultId },
                })
                return
            }

            const _version = await fetchVersion(
                'ConsultDiarizationDto',
                existing.id
            )

            await executeMutation(
                deleteConsultDiarizationDtoMutation,
                {
                    input: {
                        id: existing.id,
                        _version,
                    },
                },
                'deleteConsultDiarizationDto'
            )

            logger.info('Diarization record deleted successfully', {
                action: 'delete_diarization_success',
                metadata: { consultId },
            })
        } catch (error) {
            logger.error('Failed to delete diarization', error as Error, {
                action: 'delete_diarization_error',
                metadata: { consultId },
            })
            throw error
        }
    }
}
